import React, { useMemo } from "react";

import {
  Box,
  Checkbox,
  Table as MuiTable,
  Stack,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from "@mui/material";
import { RoleComponentGuard } from "components/guards";
import { FileViewer } from "components/popups";
import { startCase } from "lodash";
import { usePickUserPermissions } from "store";
import { useUserRoleSelector } from "store";
import { DataItem, DataroomFileTable, IFile } from "store/types";
import { isRoleAnalyst } from "utils/user";

import { RoleName } from "../../../constants";
import { FileTableRow } from "./FileTableRow";
import { SortArrows } from "./SortArrows";

interface Props {
  data: DataroomFileTable;
  sortBy: string;
  isDesc: boolean;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  setIsDesc: React.Dispatch<React.SetStateAction<boolean>>;
  selectedItems: DataItem[];
  onCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>, item: DataItem) => void;
  total_files?: number;
  paginationData: {
    page: number;
    perPage: number;
  };
  setPaginationData: (
    data: Partial<{
      page: number;
      perPage: number;
    }>
  ) => void;
  selectAll: (value: boolean) => Promise<void>;
  hideDealColumn?: boolean;
  hideAltiusDealColumn?: boolean;
  filePreview: IFile | null;
  handleCloseFilePreview: () => void;
  onFileClick: (tableRow: DataroomFileTable["rows"][0]) => Promise<void>;
}

export const Table: React.FC<Props> = ({
  data,
  sortBy,
  isDesc,
  setSortBy,
  setIsDesc,
  selectedItems,
  onCheckboxChange,
  total_files,
  paginationData,
  setPaginationData,
  selectAll,
  hideDealColumn,
  hideAltiusDealColumn,
  filePreview,
  handleCloseFilePreview,
  onFileClick
}) => {
  const cloudIntegrations = usePickUserPermissions("cloud_integrations");
  const isViewerPermission = cloudIntegrations && cloudIntegrations === "viewer";
  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPaginationData({ page: newPage });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationData({ page: 0, perPage: newRowsPerPage });
  };

  const role = useUserRoleSelector();

  const rowsPerPageOptions = useMemo(() => {
    const options = [10, 20, 30, 40, 50, 100, 200, 300];
    if (isRoleAnalyst({ role })) {
      options.push(1000);
    }
    return options;
  }, [role]);

  return (
    <>
      <TableContainer
        sx={{
          borderRadius: "8px",
          boxShadow: "none",
          border: "0.5px solid #dae0e2",
          flexGrow: 1,
          maxHeight: "calc(100% - 52px)",
          minHeight: "calc(100% - 52px)",
          overflowX: "hidden",
          overflowY: "auto"
        }}
      >
        <MuiTable stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 50 }} align="center">
                <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermission}>
                  <Checkbox
                    checked={Boolean(data.rows.length) && data.rows.length === selectedItems.length}
                    onChange={(e) => selectAll(e.target.checked)}
                    disabled={!data.rows.length}
                  />
                </RoleComponentGuard>
              </TableCell>
              {!hideDealColumn && (
                <TableCell
                  sx={headCellSx}
                  align={"left"}
                  onClick={() => {
                    if (sortBy == "fundName") {
                      setIsDesc(!isDesc);
                    }
                    setSortBy("fundName");
                  }}
                >
                  <Stack direction="row" gap="5px" alignItems="center">
                    Deal
                    <SortArrows
                      selectedSort={sortBy}
                      isDesc={isDesc}
                      sort="fundName"
                      setDesc={(data: boolean) => {
                        setIsDesc(data);
                      }}
                      setSortBy={(data: string) => {
                        setSortBy(data);
                      }}
                    />
                  </Stack>
                </TableCell>
              )}
              {!hideAltiusDealColumn && (
                <TableCell
                  sx={headCellSx}
                  align={"left"}
                  onClick={() => {
                    if (sortBy == "dealName") {
                      setIsDesc(!isDesc);
                    }
                    setSortBy("dealName");
                  }}
                >
                  <Stack direction="row" gap="5px" alignItems="center">
                    Altius Deal
                    <SortArrows
                      selectedSort={sortBy}
                      isDesc={isDesc}
                      sort="dealName"
                      setDesc={(data: boolean) => {
                        setIsDesc(data);
                      }}
                      setSortBy={(data: string) => {
                        setSortBy(data);
                      }}
                    />
                  </Stack>
                </TableCell>
              )}
              {data?.columns.map((col) => (
                <TableCell
                  sx={headCellSx}
                  key={col.key}
                  onClick={() => {
                    if (sortBy == col.key) {
                      setIsDesc(!isDesc);
                    }
                    setSortBy(col.key);
                  }}
                >
                  <Stack direction="row" gap="5px" alignItems="center">
                    {startCase(col.title)}
                    <SortArrows
                      selectedSort={sortBy}
                      isDesc={isDesc}
                      sort={col.key}
                      setDesc={(data: boolean) => {
                        setIsDesc(data);
                      }}
                      setSortBy={(data: string) => {
                        setSortBy(data);
                      }}
                    />
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows?.map((row, index) => (
              <FileTableRow
                isSelected={selectedItems.some((item) => item.id === row.id)}
                key={row.id || index}
                row={row}
                columns={data.columns}
                onCheckboxChange={onCheckboxChange}
                hideTitle={hideDealColumn}
                onFileClick={onFileClick}
              />
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0.5rem"
        }}
      >
        <Typography fontSize="15px" color="#0a3142">
          Files Selected: {selectedItems.length}
        </Typography>
        <TablePagination
          labelRowsPerPage="Rows Per Page"
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={total_files ?? 0}
          rowsPerPage={paginationData.perPage ?? 50}
          page={paginationData.page ?? 0}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ overflow: "hidden" }}
        />
      </Box>

      <FileViewer
        {...(filePreview && { file: filePreview })}
        isOpen={Boolean(filePreview)}
        onClose={handleCloseFilePreview}
      />
    </>
  );
};

const headCellSx: TableCellProps["sx"] = {
  backgroundColor: "#edf1f9",
  color: "#0a3142",
  fontSize: "15px",
  fontWeight: "normal",
  border: "none",
  height: "40px",
  whiteSpace: "nowrap",
  padding: "0px 16px"
};
